import { useOrganization } from '@features/organizations'
import { PublicUser } from '@st/sdk'
import { Button, Dialog, DialogButtons, FullPageCard, Modal } from '@st/theme'
import { isNotEmpty } from '@st/util/json-value'
import { useState } from 'react'
import { match, P } from '@st/pattern'
import { formatFolderEntityName } from '@features/st-folder-viewer/st-folder-display'
import { ClientLoginPage } from './client-login-page'
import { useClientLoginFlow } from './use-client-login-flow'
import { ClientLoginFlowState } from './client-login-flow-module'

type Props = {
  initialEmail: string | undefined
}

export function STQuestionnaireLookupPage({ initialEmail }: Props) {
  const organization = useOrganization()

  const [email, setEmail] = useState(initialEmail ?? '')
  const [useEmailHint, setUseEmailHint] = useState(isNotEmpty(initialEmail))

  const possibleUsers: PublicUser[] = isNotEmpty(email) ? [{ id: email, email, name: '' }] : []
  const emailProp = match(useEmailHint)
    .with(true, () => undefined)
    .with(false, () => ({ value: email, onChange: setEmail }))
    .exhaustive()
  const { state, send } = useClientLoginFlow()

  if (!state) {
    return <FullPageCard />
  }

  return (
    <>
      <ClientLoginPage
        organization={organization}
        title="Tax Organizer"
        subtitle="Verify your email to continue"
        possibleUsers={possibleUsers}
        email={emailProp}
        state={state}
        send={send}
      >
        <LookupFoldersAfterLogin state={state} send={send} />
      </ClientLoginPage>

      {match(state)
        .with({ status: 'lookupOrganizerNotFound' }, ({ email }) => (
          <LookupNoneFoundDialog
            clientKind="returning"
            email={email}
            actionLabel="OK"
            onClose={() => {
              setEmail('')
              setUseEmailHint(false)
              send({ type: 'tryAnotherEmail' })
            }}
          />
        ))
        .with({ status: 'lookupNoneFound' }, ({ currentUser, flow }) => (
          <LookupNoneFoundDialog
            clientKind={flow.clientKind}
            email={currentUser.email}
            actionLabel="OK"
            onClose={() => send({ type: 'logoutRequested' })}
          />
        ))
        .otherwise(() => null)}
    </>
  )
}

function LookupNoneFoundDialog({
  clientKind,
  email,
  actionLabel,
  onClose
}: {
  clientKind: 'new' | 'returning'
  email: string
  actionLabel: string
  onClose: () => void
}) {
  return (
    <Modal isOpen={true} onClose={onClose}>
      <Dialog
        title="Organizer not found"
        subtitle={lookupNoneFoundCopy(clientKind, email)}
        buttons={
          <DialogButtons>
            <Button variant="primary" onClick={onClose}>
              {actionLabel}
            </Button>
          </DialogButtons>
        }
      />
    </Modal>
  )
}

function LookupFoldersAfterLogin({
  state,
  send
}: {
  state: ClientLoginFlowState
  send: ReturnType<typeof useClientLoginFlow>['send']
}) {
  return (
    match(state)
      .with({ status: P.union('lookupPending', 'navigatingToFolder') }, () => <FullPageCard />)
      .with({ status: 'lookupFailed' }, () => (
        <div className="flex flex-col items-center gap-4">
          <p className="my-4 text-center text-base">Something went wrong. Please try again.</p>
          <Button variant="primary" onClick={() => send({ type: 'retryFolderLookup' })}>
            Retry
          </Button>
        </div>
      ))
      .with({ status: 'lookupNoneFound' }, () => <FullPageCard />)
      .with({ status: 'lookupChooseFolder' }, ({ folders }) => (
        <div className="flex flex-col items-center gap-4">
          <p className="my-4 text-center text-base">
            We found multiple questionnaires for your email address. Please select the one you want
            to open.
          </p>
          <div className="flex w-full flex-col items-stretch gap-4">
            {folders.map((f) => (
              <button
                className="flex gap-x-2 rounded bg-stone-200 px-4 py-3 text-center text-base font-medium leading-4 text-gray-900 outline-none hover:bg-stone-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:bg-stone-400"
                key={f.id}
                type="button"
                onClick={() => send({ type: 'lookupFolderChosen', folderId: f.id })}
              >
                {formatFolderEntityName(f.entities)} {f.type}
              </button>
            ))}
          </div>
        </div>
      ))
      // On non-lookup states, this component is not rendered (ClientLoginPage shows login view).
      .with(P.union({ status: 'loggedIn' }, { status: 'idle' }, { status: 'loading' }), () => (
        <FullPageCard />
      ))
      .otherwise(() => <FullPageCard />)
  )
}

function lookupNoneFoundCopy(clientKind: 'new' | 'returning', email: string) {
  return match(clientKind)
    .with(
      'returning',
      () =>
        `We couldn't find an existing organizer for ${email}. Contact your tax firm to confirm the email address or ask them to create your organizer.`
    )
    .with('new', () => `We couldn't find an organizer for ${email}.`)
    .exhaustive()
}
